import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  Row,
  Select,
  styled,
} from '@teko/ds-desktop';
import { LocationOutlined } from '@teko/ds-icon';
import { ThemeProps } from '@teko/ui';
import { useDistanceTerminalContext } from 'contexts';
import { tSelectPlaceholder } from 'helpers/translate';
import {
  useLocations,
  useLocationsV2,
  useSuggestionAddress,
  useTenantConfig,
} from 'hooks';
import { t } from 'i18n';
import { useEffect, useState } from 'react';
import { useGetDistanceTerminal } from '../useGetDistanceTerminal';
import { SwitchGetByGeoLocation } from './SwitchGetByGeoLocation';

interface AddressForm {
  address: string;
  provinceCode: string;
  wardCode: string;
  districtCode: string;
}
const COUNT_STRING_WITHOUT_STREET_ADDRESS = 4;
export const ModeEnterAddress = () => {
  const { epicOmni1589 } = useTenantConfig();
  return (
    <>
      <SwitchGetByGeoLocation />
      {epicOmni1589 ? <FormAddressV2 /> : <FormAddressV1 />}
    </>
  );
};

const Wrapper = styled.div`
  height: 33vh;
  @media (max-width: 1600px) {
    height: 300px;
  }
`;

const SubTitle = styled.span`
  font-size: 14px;
  font-weight: 400;
`;

const Text = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #434657;
`;

export const Spinner = styled.div<ThemeProps>(
  ({ theme }) => `
  border: 3px solid ${theme.colors.blue100};
  border-top-color: ${theme.colors.blue500};
  border-radius: 50%;
  width: 1.5em;
  height: 1.5em;
  margin: 0 0.5rem;
  animation: spin 1s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`,
);

export const WrapperError = styled.div<ThemeProps>(
  ({ theme }) => `
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${theme.colors.warning};
  background-color: #FFFBE6;
  padding: 8px;
`,
);

export const SuggestWrapper = styled.div`
  background-color: #fafafa;
  border-radius: 12px;
  padding: 8px;
  margin-bottom: 15px;
  max-height: 200px;
  overflow-y: scroll;
`;

export const ItemSuggestion = styled.div`
  padding: 8px 10px;
  cursor: pointer;
`;

const FormAddressV1 = () => {
  const [form] = Form.useForm<AddressForm>();
  const { epicOmni1584Phase2SuggestAddress } = useTenantConfig();
  const [place, setPlace] = useState<{ lat?: number; long?: number }>({
    lat: undefined,
    long: undefined,
  });
  const {
    locationGetDistanceTerminals,
    isLoadingGetDistanceTerminals,
    messageError,
    isGetCurrentLocation,
  } = useDistanceTerminalContext();

  const { getDistanceTerminalsByGeoLocation, getDistanceTerminalsByAddress } =
    useGetDistanceTerminal();

  const {
    provinces = [],
    wards = [],
    province,
    ward,
    onProvinceChanged,
    onWardChanged,
    district,
    districts = [],
    onDistrictChanged,
    setLocation,
  } = useLocations({
    provinceCode: locationGetDistanceTerminals?.provinceCode,
    provinceName: locationGetDistanceTerminals?.provinceName,
    districtCode: locationGetDistanceTerminals?.districtCode,
    districtName: locationGetDistanceTerminals?.districtName,
    wardCode: locationGetDistanceTerminals?.wardCode,
    wardName: locationGetDistanceTerminals?.wardName,
  });

  const getDistanceTerminalsByAddressForm = async (data: AddressForm) => {
    const provinceName =
      provinces.find((province) => province.code === data.provinceCode)?.name ||
      '';
    const wardName =
      wards.find((ward) => ward.code === data.wardCode)?.name || '';

    const districtName =
      districts.find((district) => district.code === data.districtCode)?.name ||
      '';
    if (!provinceName || !wardName || !districtName) {
      const provinceError = !provinceName
        ? [
            {
              name: 'provinceCode',
              errors: [`${t('Tỉnh/thành không được bỏ trống')}`],
            },
          ]
        : [];
      const districtError = !districtName
        ? [
            {
              name: 'districtCode',
              errors: [`${t('Phường/xã không được bỏ trống')}`],
            },
          ]
        : [];
      const wardError = !wardName
        ? [
            {
              name: 'wardCode',
              errors: [`${t('Quận/huyện không được bỏ trống')}`],
            },
          ]
        : [];
      form.setFields([...provinceError, ...districtError, ...wardError]);
      return;
    }
    getDistanceTerminalsByAddress({
      ...data,
      provinceName,
      wardName,
      districtName,
      lat: place.lat,
      long: place.long,
    });
  };

  const {
    dataSuggestion,
    setSuggestInputText,
    openListSuggestion,
    setOpenListSuggestion,
    isLoadingSuggest,
    getPlaceDetails,
  } = useSuggestionAddress({
    province: provinces?.find((p) => p.code === province?.code)?.name || '',
    district: districts?.find((d) => d.code === district?.code)?.name || '',
    ward: wards?.find((w) => w.code === ward?.code)?.name || '',
  });

  useEffect(() => {
    if (!!locationGetDistanceTerminals) {
      form.setFieldsValue({
        provinceCode: locationGetDistanceTerminals.provinceCode || '',
        districtCode: locationGetDistanceTerminals.districtCode || '',
        wardCode: locationGetDistanceTerminals.wardCode || '',
        address: locationGetDistanceTerminals.address || '',
      });
      setLocation({
        provinceCode: locationGetDistanceTerminals.provinceCode,
        districtCode: locationGetDistanceTerminals.districtCode || '',
        wardCode: locationGetDistanceTerminals.wardCode,
      });
      setPlace({
        lat: locationGetDistanceTerminals.lat,
        long: locationGetDistanceTerminals.long,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationGetDistanceTerminals]);

  const onClickSuggest = async (placeId: string) => {
    const items = await getPlaceDetails(placeId);
    if (!items || !Array.isArray(items) || !items.length) {
      return;
    }
    // items là danh sách các placeDetail và cần lấy placeDetail đầu tiên
    // trong case này chỉ gửi 1 placeID lên nên chỉ có 1 placeDetail
    const item = items?.[0];
    const address =
      item.fullAddress
        .split(',')
        ?.splice(
          0,
          item.fullAddress.split(',').length -
            COUNT_STRING_WITHOUT_STREET_ADDRESS,
        )
        ?.join(',') ||
      item.fullAddress ||
      '';
    setOpenListSuggestion(false);
    setPlace({ lat: item.lat, long: item.long });
    const newValue = {
      provinceCode: item.provinceCode || undefined,
      ...(item.districtCode ? { districtCode: item.districtCode } : {}),
      ...(item.wardCode ? { wardCode: item.wardCode } : {}),
    };
    form.setFieldsValue({ ...newValue, address });
    if (!!item.provinceCode && !!item.districtCode && !!item.wardCode) {
      setLocation({
        provinceCode: item.provinceCode,
        districtCode: item.districtCode,
        wardCode: item.wardCode,
      });
    }
  };

  const resetPosition = () => {
    setPlace({ lat: undefined, long: undefined });
  };

  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={
        isGetCurrentLocation
          ? getDistanceTerminalsByGeoLocation
          : getDistanceTerminalsByAddressForm
      }
    >
      <Wrapper
        style={{
          height:
            !!dataSuggestion && !!openListSuggestion && !!dataSuggestion.length
              ? 546
              : 346,
        }}
      >
        <>
          <SubTitle style={{ textAlign: 'left' }}>
            {t('Hoặc nhập địa chỉ ')}
          </SubTitle>
          <Row justify="space-between" marginBottom="s8">
            <Col flex="49%">
              <Form.Item
                label={t('Tỉnh/Thành phố')}
                trigger="selectValue"
                name="provinceCode"
                rules={[
                  {
                    required: !isGetCurrentLocation,
                    message: t('${label} không được bỏ trống'),
                  },
                ]}
                validateTrigger="selectValue"
              >
                <Select
                  options={provinces?.map(({ name, code }) => ({
                    text: name,
                    value: code,
                  }))}
                  value={province?.code || ''}
                  maxPerView={5}
                  placeholder={tSelectPlaceholder('Tỉnh/Thành phố')}
                  selectValue={(value) => {
                    const location = provinces?.find(
                      (prov) => prov.code === value,
                    );
                    if (location) {
                      onProvinceChanged(location.code);
                      form.setFieldsValue({
                        provinceCode: location.code,
                        districtCode: '',
                        wardCode: '',
                      });
                      onDistrictChanged('');
                      onWardChanged('');
                      resetPosition();
                    }
                  }}
                  disabled={isGetCurrentLocation}
                  onDelete={() => {
                    form.setFieldsValue({ provinceCode: '', wardCode: '' });
                    onProvinceChanged('');
                    resetPosition();
                  }}
                />
              </Form.Item>
            </Col>
            <Col flex="49%">
              <Form.Item
                label={t('Quận Huyện')}
                trigger="selectValue"
                name="districtCode"
                rules={[
                  {
                    required: !isGetCurrentLocation,
                    message: t('${label} không được bỏ trống'),
                  },
                ]}
                validateTrigger="selectValue"
              >
                <Select
                  options={districts?.map(({ name, code }) => ({
                    text: name,
                    value: code,
                  }))}
                  value={district?.code || ''}
                  maxPerView={5}
                  placeholder={tSelectPlaceholder('Quận/ Huyện')}
                  selectValue={(value) => {
                    const location = districts?.find(
                      (district) => district.code === value,
                    );
                    if (location) {
                      onDistrictChanged(location.code);
                      form.setFieldsValue({
                        districtCode: location.code,
                        wardCode: '',
                      });
                      onWardChanged('');
                      resetPosition();
                    }
                  }}
                  disabled={isGetCurrentLocation}
                  onDelete={() => {
                    form.setFieldsValue({ districtCode: '' });
                    onWardChanged('');
                    resetPosition();
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="space-between" marginBottom="s8">
            <Col flex="49%">
              <Form.Item
                label={t('Phường/Xã')}
                trigger="selectValue"
                name="wardCode"
                rules={[
                  {
                    required: !isGetCurrentLocation,
                    message: t('${label} không được bỏ trống'),
                  },
                ]}
                validateTrigger="selectValue"
              >
                <Select
                  options={wards?.map(({ name, code }) => ({
                    text: name,
                    value: code,
                  }))}
                  value={ward?.code || ''}
                  maxPerView={5}
                  placeholder={tSelectPlaceholder('Phường/Xã')}
                  selectValue={(value) => {
                    const location = wards?.find((ward) => ward.code === value);
                    if (location) {
                      onWardChanged(location.code);
                      form.setFieldsValue({
                        wardCode: location.code,
                      });
                      resetPosition();
                    }
                  }}
                  disabled={isGetCurrentLocation}
                  onDelete={() => {
                    form.setFieldsValue({ wardCode: '' });
                    onWardChanged('');
                    resetPosition();
                  }}
                />
              </Form.Item>
            </Col>
            <Col flex="49%">
              <Form.Item
                label={t('Địa chỉ cụ thể')}
                name="address"
                rules={[
                  {
                    required: !isGetCurrentLocation,
                    message: t('${label} không được bỏ trống'),
                  },
                ]}
              >
                <Input
                  disabled={
                    isGetCurrentLocation ||
                    ((!province?.code || !district?.code || !ward?.code) &&
                      epicOmni1584Phase2SuggestAddress)
                  }
                  placeholder={t('Số nhà, ngõ, tên đường...')}
                  onChange={(text) => {
                    setSuggestInputText(text);
                    setOpenListSuggestion(true);
                    resetPosition();
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </>
        {epicOmni1584Phase2SuggestAddress &&
          !!dataSuggestion &&
          !!openListSuggestion &&
          (!!dataSuggestion.length || isLoadingSuggest) && (
            <SuggestWrapper>
              {isLoadingSuggest ? (
                <Flex justify="center" align="center">
                  <Spinner />
                </Flex>
              ) : (
                <>
                  <Text style={{ textAlign: 'left' }}>{t('Gợi ý')}</Text>
                  {dataSuggestion.map((item) => {
                    return (
                      <ItemSuggestion
                        key={item.fullAddress}
                        onClick={() => onClickSuggest(item.placeId)}
                      >
                        <LocationOutlined />
                        <Text style={{ textAlign: 'left', marginLeft: 8 }}>
                          {item.fullAddress}
                        </Text>
                      </ItemSuggestion>
                    );
                  })}
                </>
              )}
            </SuggestWrapper>
          )}
        {messageError && <WrapperError>{messageError}</WrapperError>}
        <Button
          disabled={isLoadingGetDistanceTerminals}
          block
          type="submit"
          variant="primary"
        >
          {isLoadingGetDistanceTerminals && <Spinner />}
          {t('Xác nhận')}
        </Button>
      </Wrapper>
    </Form>
  );
};
const FormAddressV2 = () => {
  const [form] = Form.useForm<AddressForm>();
  const [place, setPlace] = useState<{ lat?: number; long?: number }>({
    lat: undefined,
    long: undefined,
  });
  const { epicOmni1584Phase2SuggestAddress } = useTenantConfig();

  const {
    locationGetDistanceTerminals,
    isLoadingGetDistanceTerminals,
    messageError,
    isGetCurrentLocation,
  } = useDistanceTerminalContext();

  const { getDistanceTerminalsByGeoLocation, getDistanceTerminalsByAddress } =
    useGetDistanceTerminal();

  const {
    provinces = [],
    wards = [],
    province,
    ward,
    onProvinceChanged,
    onWardChanged,
  } = useLocationsV2(
    locationGetDistanceTerminals?.provinceCode,
    locationGetDistanceTerminals?.wardCode,
  );

  const getDistanceTerminalsByAddressForm = async (data: AddressForm) => {
    const provinceName =
      provinces.find((province) => province.code === data.provinceCode)
        ?.title || '';
    const wardName =
      wards.find((ward) => ward.code === data.wardCode)?.title || '';
    getDistanceTerminalsByAddress({
      ...data,
      provinceName,
      wardName,
      lat: place.lat,
      long: place.long,
    });
  };

  useEffect(() => {
    if (!!locationGetDistanceTerminals) {
      form.setFieldsValue({
        provinceCode: locationGetDistanceTerminals.provinceCode || '',
        wardCode: locationGetDistanceTerminals.wardCode || '',
        address: locationGetDistanceTerminals.address || '',
      });
      onProvinceChanged(locationGetDistanceTerminals.provinceCode);
      onWardChanged(locationGetDistanceTerminals.wardCode);
      setPlace({
        lat: locationGetDistanceTerminals.lat,
        long: locationGetDistanceTerminals.long,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationGetDistanceTerminals]);

  const {
    dataSuggestion,
    setSuggestInputText,
    openListSuggestion,
    setOpenListSuggestion,
    isLoadingSuggest,
    getPlaceDetails,
  } = useSuggestionAddress({
    province: provinces.find((p) => p.code === province?.code)?.title || '',
    ward: wards.find((w) => w.code === ward?.code)?.title || '',
  });
  //
  const onClickSuggest = async (placeId: string) => {
    const items = await getPlaceDetails(placeId);
    if (!items || !Array.isArray(items) || !items.length) {
      return;
    }
    // items là danh sách các placeDetail và cần lấy placeDetail đầu tiên
    // trong case này chỉ gửi 1 placeID lên nên chỉ có 1 placeDetail
    const item = items?.[0];
    const address =
      item.fullAddress
        .split(',')
        ?.splice(
          0,
          item.fullAddress.split(',').length -
            COUNT_STRING_WITHOUT_STREET_ADDRESS,
        )
        ?.join(',') ||
      item.fullAddress ||
      '';
    setOpenListSuggestion(false);
    setPlace({ lat: item.lat, long: item.long });
    const newValue = {
      provinceCode: item.provinceCode || undefined,
      ...(item.wardCode ? { wardCode: item.wardCode } : {}),
    };
    form.setFieldsValue({
      address,
      ...newValue,
    });
  };

  const resetPosition = () => {
    setPlace({ lat: undefined, long: undefined });
  };

  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={
        isGetCurrentLocation
          ? getDistanceTerminalsByGeoLocation
          : getDistanceTerminalsByAddressForm
      }
    >
      <Wrapper
        style={{
          height:
            !!dataSuggestion && !!openListSuggestion && !!dataSuggestion.length
              ? 546
              : 346,
        }}
      >
        <>
          <SubTitle style={{ textAlign: 'left' }}>
            {t('Hoặc nhập địa chỉ ')}
          </SubTitle>
          <Row justify="space-between" marginBottom="s8">
            <Col flex="49%">
              <Form.Item
                label={t('Tỉnh/Thành phố')}
                trigger="selectValue"
                name="provinceCode"
                rules={[
                  {
                    required: !isGetCurrentLocation,
                    message: t('${label} không được bỏ trống'),
                  },
                ]}
                validateTrigger="selectValue"
              >
                <Select
                  options={provinces?.map(({ title, code }) => ({
                    text: title,
                    value: code,
                  }))}
                  value={province?.code || ''}
                  maxPerView={5}
                  placeholder={tSelectPlaceholder('Tỉnh/Thành phố')}
                  selectValue={(value) => {
                    const location = provinces?.find(
                      (prov) => prov.code === value,
                    );
                    if (location) {
                      onProvinceChanged(location.code);
                      form.setFieldsValue({
                        provinceCode: location.code,
                        wardCode: '',
                      });
                      onWardChanged('');
                      resetPosition();
                    }
                  }}
                  disabled={isGetCurrentLocation}
                  onDelete={() => {
                    form.setFieldsValue({ provinceCode: '', wardCode: '' });
                    onProvinceChanged('');
                    resetPosition();
                  }}
                />
              </Form.Item>
            </Col>
            <Col flex="49%">
              <Form.Item
                label={t('Phường/Xã')}
                trigger="selectValue"
                name="wardCode"
                rules={[
                  {
                    required: !isGetCurrentLocation,
                    message: t('${label} không được bỏ trống'),
                  },
                ]}
                validateTrigger="selectValue"
              >
                <Select
                  options={wards.map(({ title, code }) => ({
                    text: title,
                    value: code,
                  }))}
                  value={ward?.code || ''}
                  maxPerView={5}
                  placeholder={tSelectPlaceholder('Phường/Xã')}
                  selectValue={(value) => {
                    const location = wards?.find((ward) => ward.code === value);
                    if (location) {
                      onWardChanged(location.code);
                      form.setFieldsValue({
                        wardCode: location.code,
                      });
                      resetPosition();
                    }
                  }}
                  onDelete={() => {
                    form.setFieldsValue({ wardCode: '' });
                    onWardChanged('');
                    resetPosition();
                  }}
                  disabled={isGetCurrentLocation}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label={t('Địa chỉ cụ thể')}
            name="address"
            rules={[
              {
                required: !isGetCurrentLocation,
                message: t('${label} không được bỏ trống'),
              },
            ]}
          >
            <Input
              disabled={isGetCurrentLocation}
              placeholder={t('Số nhà, ngõ, tên đường...')}
              onChange={(text) => {
                setSuggestInputText(text);
                setOpenListSuggestion(true);
              }}
            />
          </Form.Item>
          {epicOmni1584Phase2SuggestAddress &&
            !!dataSuggestion &&
            !!openListSuggestion &&
            (!!dataSuggestion.length || isLoadingSuggest) && (
              <SuggestWrapper>
                {isLoadingSuggest ? (
                  <Flex justify="center" align="center">
                    <Spinner />
                  </Flex>
                ) : (
                  <>
                    <Text style={{ textAlign: 'left' }}>{t('Gợi ý')}</Text>
                    {dataSuggestion.map((item) => {
                      return (
                        <ItemSuggestion
                          key={item.fullAddress}
                          onClick={() => onClickSuggest(item.placeId)}
                        >
                          <LocationOutlined />
                          <Text style={{ textAlign: 'left', marginLeft: 8 }}>
                            {item.fullAddress}
                          </Text>
                        </ItemSuggestion>
                      );
                    })}
                  </>
                )}
              </SuggestWrapper>
            )}
        </>

        {messageError && <WrapperError>{messageError}</WrapperError>}
        <Button
          disabled={isLoadingGetDistanceTerminals}
          block
          type="submit"
          variant="primary"
        >
          {isLoadingGetDistanceTerminals && <Spinner />}
          {t('Xác nhận')}
        </Button>
      </Wrapper>
    </Form>
  );
};
